import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { IconSizes, IconVariants } from '../../components/foundations/Icon';
import AutoComplete from '../../components/molecules/AutoComplete';
import { Icon } from '../../components/widgets';
import AddFutureItems from '../containers/AddFutureItems';
import { translationPrefix } from '../utils';
import css from './PartnerPageStep.css';
import PicturizedMultiSelect from './PicturizedMultiSelect';
import StepCard from './StepCard';

const iconProps = {
    size: IconSizes.X_SMALL,
    variant: IconVariants.LIGHT,
    interactive: false,
};

const PartnerPageStep = ({
    partners = [],
    membersPerPartner = {},
    onChange = () => {},
    onAllChange = () => {},
    enableAddFuture = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
}) => {
    const [partnerFilter, setPartnerFilter] = useState('');
    const selectedPartnersCount = partners.filter(partner => partner.selected).length;
    const disabledPartnersCount = partners.filter(partner => partner.disabled).length;
    const totalAvailablePartnersCount = partners.length;

    useEffect(() => {
        const isValid = selectedPartnersCount > 0;
        onStepValidationStatusChanged({
            isValid,
            title: isValid
                ? ''
                : translate('STATIC.PAGES.TEAM_MEMBERS.SELECT_AT_LEAST_ONE_TOOLTIP', {
                      itemType: 'partner',
                  }),
        });
    }, [onStepValidationStatusChanged, translate, selectedPartnersCount]);

    const selectAll = value => {
        onAllChange(
            partners.filter(partner => !partner.disabled).map(partner => partner.id),
            value
        );
        if (value) {
            enableAddFuture();
        }
    };

    const onItemSelected = (selectedId, value) => {
        const partner = partners.find(({ id }) => id === selectedId);
        if (partner) {
            onChange(partner.id, value);
        }
    };

    const textPredicate = partner =>
        partner.name.toLocaleLowerCase().includes(partnerFilter.toLocaleLowerCase().trim());

    const partnerItems = useMemo(
        () =>
            partners.map(partner => {
                let extra = null;
                const inUseBy = membersPerPartner[partner.name] || [];

                if (partner.disabled) {
                    extra = (
                        <div className={css.extra}>
                            <Icon name="ban" {...iconProps} />
                            <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.SAN_PARTNER_NOT_SUPPORTED`} />
                        </div>
                    );
                } else if (inUseBy.length > 0) {
                    const displayNamesCount = 2;
                    let names = inUseBy.slice(0, displayNamesCount).join(', ');
                    if (inUseBy.length > displayNamesCount) {
                        names += `, +${inUseBy.length - displayNamesCount}`;
                    }
                    extra = (
                        <div className={css.extra}>
                            <Icon name="group-3-line" {...iconProps} />
                            <Translate id={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.IN_USE_BY`} data={{ names }} />
                        </div>
                    );
                }

                return {
                    ...partner,
                    extra,
                    ...(partner.icon && {
                        Icon: 'img',
                        iconProps: { src: partner.icon, alt: partner.name },
                    }),
                };
            }),
        [partners, membersPerPartner]
    );

    return (
        <div className={css.container}>
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.PARTNERS`}
                titleTextReplace={{
                    count: selectedPartnersCount,
                    total: totalAvailablePartnersCount - disabledPartnersCount,
                }}
                description={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.DESCRIPTION.PARTNERS`}
            >
                <div>
                    <AutoComplete
                        placeholder={translate(`${translationPrefix}.PLACEHOLDER.PARTNER_SEARCH`)}
                        selectOptions={{
                            options: [],
                            noOptionsMessage: () => null,
                            isSearchable: true,
                            inputValue: partnerFilter,
                        }}
                        onInputChange={setPartnerFilter}
                        controlledValue={partnerFilter}
                    />
                </div>
                <PicturizedMultiSelect
                    className={css.multiSelectContainer}
                    allItems={partnerItems}
                    filteredItems={partnerItems.filter(textPredicate)}
                    onChange={onItemSelected}
                    selectAll={selectAll}
                    itemType="partners"
                    sorted
                />
            </StepCard>
            <AddFutureItems itemType="partner" form="base_source" dimensionFilter className={css.addFutureItems} />
        </div>
    );
};

PartnerPageStep.propTypes = {
    partners: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string.isRequired,
            logoPath: PropTypes.string.isRequired,
            selected: PropTypes.bool.isRequired,
        })
    ),
    membersPerPartner: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
    onChange: PropTypes.func,
    onAllChange: PropTypes.func,
    enableAddFuture: PropTypes.func,
    translate: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
};

export default PartnerPageStep;
